/**
 * Medewerkers — galerij met foto, naam en functies (gap-analyse #8, 26 sep 2026).
 * Bron: Personen in de Leveranciers-base (Airtable), via de engine-spiegel.
 *
 * Iedereen ziet Medewerkers en Stagiairs. Het Archief (oud-collega's) en de filters
 * Magazijnsleutel en Rijbewijs B zijn voor HR (recht 'hr_leveranciers'); de server
 * stuurt die velden anders niet eens mee.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useUserRole } from '../../contexts/UserRoleContext';
import { filterGalerij, alleFuncties } from '../Leveranciers/hrRekenen';
import { ACCENT, INK, Portret, bijgewerktTekst, haalJson } from '../Leveranciers/hrUi';

const TABS = [
  { key: 'medewerkers', label: 'Medewerkers' },
  { key: 'stagiairs', label: 'Stagiairs' },
  { key: 'archief', label: 'Archief', hr: true },
];

export default function Medewerkers() {
  const { permissions } = useUserRole();
  const isHr = !!permissions?.hr_leveranciers?.view;
  const magOnboarden = !!permissions?.admin_settings?.view;
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab') && (!t.hr || isHr)) ? params.get('tab') : 'medewerkers';

  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filters, setFilters] = useState({ functie: '', magazijnsleutel: '', rijbewijs: '', zoek: '' });

  const laad = useCallback(async () => {
    setLaden(true); setFout(null);
    try {
      setData(await haalJson(`${BACKEND_URL}/Api/medewerkers?tab=${tab}`, 'Medewerkers laden'));
    } catch (e) {
      setFout(e.message);
    } finally {
      setLaden(false);
    }
  }, [tab]);
  useEffect(() => { laad(); }, [laad]);

  const personen = useMemo(() => data?.personen || [], [data]);
  const functies = useMemo(() => alleFuncties(personen), [personen]);
  const zichtbaar = useMemo(() => filterGalerij(personen, filters), [personen, filters]);
  const hrVelden = !!data?.hr;
  const zet = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="groups"
        title="Medewerkers"
        subtitle="Wie werkt er bij Full Frame — foto, naam en functies"
        accent={ACCENT}
        rightActions={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isHr && <Button component={RouterLink} to="/hr-dashboard" sx={{ textTransform: 'none' }}>HR-dashboard</Button>}
            {magOnboarden && (
              <Button variant="outlined" component={RouterLink} to="/admin/settings?tab=users&section=onboarding" sx={{ textTransform: 'none' }}>
                Nieuwe medewerker
              </Button>
            )}
          </Box>
        )}
      />

      <Tabs value={tab} onChange={(_e, v) => setParams(v === 'medewerkers' ? {} : { tab: v })}
        sx={{ mb: 2, borderBottom: `1px solid ${INK.lijn}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        {TABS.filter((t) => !t.hr || isHr).map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3, alignItems: 'center' }}>
        <TextField size="small" label="Zoek op naam" value={filters.zoek} onChange={zet('zoek')} sx={{ width: 200 }} />
        <TextField select size="small" label="Functie" value={filters.functie} onChange={zet('functie')} sx={{ width: 200 }}>
          <MenuItem value="">Alle functies</MenuItem>
          {functies.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
        </TextField>
        {hrVelden && (
          <>
            <TextField select size="small" label="Magazijnsleutel" value={filters.magazijnsleutel} onChange={zet('magazijnsleutel')} sx={{ width: 170 }}>
              <MenuItem value="">Maakt niet uit</MenuItem>
              <MenuItem value="ja">Heeft sleutel</MenuItem>
              <MenuItem value="nee">Geen sleutel</MenuItem>
            </TextField>
            <TextField select size="small" label="Rijbewijs B" value={filters.rijbewijs} onChange={zet('rijbewijs')} sx={{ width: 170 }}>
              <MenuItem value="">Maakt niet uit</MenuItem>
              <MenuItem value="ja">Heeft rijbewijs</MenuItem>
              <MenuItem value="nee">Geen rijbewijs</MenuItem>
            </TextField>
          </>
        )}
        <Typography sx={{ fontSize: 12, color: INK.stil, ml: 'auto' }}>
          {!laden && `${zichtbaar.length} van ${personen.length} · `}{bijgewerktTekst(data?.bijgewerkt)}
        </Typography>
      </Box>

      {fout && <Alert severity="error" action={<Button color="inherit" size="small" onClick={laad}>Opnieuw</Button>}>{fout}</Alert>}
      {laden ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !fout && zichtbaar.length === 0 ? (
        <Typography sx={{ color: INK.zacht, py: 4 }}>
          {personen.length ? 'Niemand past bij deze filters.' : 'Nog niemand in deze lijst — de spiegel uit Airtable is leeg.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 3 }}>
          {zichtbaar.map((p) => (
            <Box key={p.id} sx={{ textAlign: 'center', px: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.25 }}>
                <Portret naam={p.naam} foto={p.foto} maat={112} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: INK.tekst, fontSize: 15 }}>{p.naam}</Typography>
              <Typography sx={{ fontSize: 13, color: INK.zacht }}>{(p.functies || []).join(', ') || '—'}</Typography>
              {hrVelden && (p.magazijnsleutel || ['Schakel', 'Automaat'].includes(p.rijbewijsB)) && (
                <Typography sx={{ fontSize: 12, color: INK.stil, mt: 0.25 }}>
                  {[p.magazijnsleutel && 'sleutel', ['Schakel', 'Automaat'].includes(p.rijbewijsB) && `rijbewijs (${p.rijbewijsB.toLowerCase()})`].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
